import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Routes, Route, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import {
  LayoutDashboard, Users, FileText, Activity, Shield, Loader2,
  Ban, Trash2, Flag, CheckCircle2, Search, ChevronLeft, ChevronRight,
  ArrowLeft, LogOut, AlertCircle, Globe,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// ---- Role helpers ----
const ROLE_RANK = { support_manager: 1, admin: 2, super_admin: 3 };
const can = (role, min) => (ROLE_RANK[role] || 0) >= (ROLE_RANK[min] || 0);

// ---- Shared primitives ----
const Eyebrow = ({ children, klein = false }) => (
  <div className={`font-mono text-[10px] tracking-[0.22em] uppercase ${klein ? "text-[#002FA7]" : "text-[#5C5C5C]"}`}>
    {children}
  </div>
);
const Card = ({ children, className = "" }) => (
  <div className={`border border-[#0A0A0A]/12 bg-[#F9F8F6] ${className}`}>{children}</div>
);
const KCard = ({ label, value, hint, accent = false }) => (
  <Card className={`p-5 ${accent ? "bg-[#0A0A0A] text-[#F9F8F6] border-[#0A0A0A]" : ""}`}>
    <div className={`font-mono text-[10px] tracking-[0.22em] uppercase ${accent ? "text-[#F9F8F6]/55" : "text-[#5C5C5C]"}`}>
      {label}
    </div>
    <div className="font-serif text-4xl font-light leading-none mt-2 tracking-tight">
      {value ?? "—"}
    </div>
    {hint && (
      <div className={`font-mono text-[10px] tracking-[0.18em] uppercase mt-2 ${accent ? "text-[#F9F8F6]/55" : "text-[#5C5C5C]"}`}>
        {hint}
      </div>
    )}
  </Card>
);

const PillButton = ({ children, onClick, disabled, tone = "default", "data-testid": tid }) => {
  const tones = {
    default: "border border-[#0A0A0A]/25 hover:border-[#002FA7] hover:text-[#002FA7]",
    danger: "border border-[#0A0A0A] bg-[#0A0A0A] text-[#F9F8F6] hover:bg-[#7a0e0e] hover:border-[#7a0e0e]",
    klein: "bg-[#002FA7] text-white hover:bg-[#0A0A0A]",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={tid}
      className={`font-mono text-[10px] tracking-[0.22em] uppercase px-3 py-2 disabled:opacity-40 flex items-center gap-2 ${tones[tone]}`}
    >
      {children}
    </button>
  );
};

const Status = ({ value }) => {
  const v = value || "active";
  const tone =
    v === "banned" ? "bg-[#7a0e0e] text-white"
    : v === "suspended" ? "bg-[#EFEFE8] text-[#0A0A0A]"
    : "bg-[#0A0A0A] text-[#F9F8F6]";
  return (
    <span className={`font-mono text-[9px] tracking-[0.22em] uppercase px-2 py-1 ${tone}`}>{v}</span>
  );
};

const Spinner = () => <Loader2 className="w-4 h-4 animate-spin" />;

const ErrorBlock = ({ children }) => (
  <div className="border border-[#0A0A0A] bg-[#0A0A0A] text-[#F9F8F6] p-4 font-mono text-xs flex items-center gap-2">
    <AlertCircle className="w-4 h-4" /> {children}
  </div>
);

// ---- Pages ----
const Dashboard = () => {
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState(null);
  const [activity, setActivity] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/overview`),
      axios.get(`${API}/admin/series/daily?days=14`),
      axios.get(`${API}/admin/activity?limit=12`),
    ])
      .then(([o, s, a]) => { setOverview(o.data); setSeries(s.data.series); setActivity(a.data.items); })
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, []);

  if (err) return <ErrorBlock>{err}</ErrorBlock>;
  if (!overview) return <div className="font-mono text-xs text-[#5C5C5C] tracking-widest uppercase py-20 text-center">Loading…</div>;

  return (
    <div className="space-y-10">
      <div>
        <Eyebrow klein>Overview</Eyebrow>
        <h2 className="font-serif text-4xl font-light tracking-tight mt-1">Platform pulse.</h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KCard accent label="Total Users" value={overview.total_users} hint={`${overview.banned_users || 0} banned`} />
        <KCard label="New Signups · Today" value={overview.new_signups_today} hint={`${overview.active_sessions} active sessions`} />
        <KCard label="Blueprints · Total" value={overview.blueprints_total} hint={`${overview.blueprints_today} today`} />
        <KCard label="Quick Evaluations" value={overview.evaluations_total} hint={`${overview.evaluations_today} today`} />
        <KCard label="Failed Jobs" value={overview.jobs_failed_total} />
        <KCard label="Flagged Blueprints" value={overview.blueprints_flagged} />
        <KCard label="Subscriptions" value="—" hint="Awaiting Stripe" />
        <KCard label="MRR" value="—" hint="Awaiting Stripe" />
      </div>

      <Card className="p-5">
        <Eyebrow klein>14-Day Activity</Eyebrow>
        <h3 className="font-serif text-2xl font-light tracking-tight mb-4 mt-1">Signups · Blueprints · Evaluations.</h3>
        <div style={{ width: "100%", height: 320, minHeight: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series || []} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
              <CartesianGrid stroke="#0A0A0A14" strokeDasharray="2 4" />
              <XAxis dataKey="date" stroke="#5C5C5C" fontSize={10} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#5C5C5C" fontSize={10} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#0A0A0A", color: "#F9F8F6", border: "1px solid #0A0A0A", fontFamily: "IBM Plex Mono, monospace", fontSize: 10 }}
                cursor={{ stroke: "#002FA7", strokeWidth: 1 }}
              />
              <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase" }} />
              <Line type="monotone" dataKey="signups" stroke="#002FA7" strokeWidth={2} dot={{ r: 2 }} />
              <Line type="monotone" dataKey="blueprints" stroke="#0A0A0A" strokeWidth={2} dot={{ r: 2 }} />
              <Line type="monotone" dataKey="evaluations" stroke="#5C5C5C" strokeWidth={2} dot={{ r: 2 }} strokeDasharray="3 3" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card>
        <div className="p-5 border-b border-[#0A0A0A]/12">
          <Eyebrow klein>Recent Activity</Eyebrow>
          <h3 className="font-serif text-2xl font-light tracking-tight mt-1">Audit log.</h3>
        </div>
        <ul className="divide-y divide-[#0A0A0A]/8">
          {activity.length === 0 && (
            <li className="px-5 py-6 text-sm text-[#5C5C5C] font-mono">No admin actions logged yet.</li>
          )}
          {activity.map((a) => (
            <li key={a.id} className="px-5 py-3 flex items-baseline gap-4">
              <span className="font-mono text-[10px] text-[#5C5C5C] tracking-[0.18em] uppercase w-44 shrink-0">
                {new Date(a.created_at).toLocaleString()}
              </span>
              <span className="font-mono text-[10px] text-[#002FA7] tracking-[0.18em] uppercase w-36 shrink-0">{a.action}</span>
              <span className="text-sm text-[#0A0A0A]/85 truncate">
                <strong>{a.admin_email}</strong>{a.target ? <> · target <code className="font-mono text-xs">{a.target}</code></> : null}
              </span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
};

const UsersPage = ({ role }) => {
  const [data, setData] = useState({ items: [], total: 0, skip: 0, limit: 25 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [skip, setSkip] = useState(0);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = (s = skip) => {
    setErr(null);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    params.set("skip", s);
    params.set("limit", 25);
    axios.get(`${API}/admin/users?${params}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  };

  useEffect(() => { load(0); setSkip(0); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { load(skip); /* eslint-disable-next-line */ }, [skip]);

  const setStatusFor = async (userId, newStatus) => {
    setBusy(userId + ":" + newStatus);
    try {
      await axios.post(`${API}/admin/users/${userId}/status`, { status: newStatus });
      load(skip);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  };
  const deleteUser = async (userId) => {
    if (!window.confirm("Permanently delete this user and all their sessions? Their blueprints will remain.")) return;
    setBusy(userId + ":delete");
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      load(skip);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-6">
      <div>
        <Eyebrow klein>Users</Eyebrow>
        <h2 className="font-serif text-4xl font-light tracking-tight mt-1">All users — {data.total}.</h2>
      </div>

      <Card className="p-4 flex flex-wrap gap-3">
        <form onSubmit={(e) => { e.preventDefault(); setSkip(0); load(0); }} className="flex items-center border border-[#0A0A0A]/15 flex-1 min-w-[260px]">
          <Search className="w-4 h-4 mx-3 text-[#5C5C5C]" />
          <input
            data-testid="users-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email, name, user_id…"
            className="bg-transparent py-2.5 outline-none font-sans text-sm flex-1"
          />
          <button type="submit" className="font-mono text-[10px] tracking-[0.18em] uppercase px-4 py-2 text-[#5C5C5C] hover:text-[#002FA7]">Go</button>
        </form>
        <select
          data-testid="users-status-filter"
          value={status} onChange={(e) => setStatus(e.target.value)}
          className="border border-[#0A0A0A]/15 bg-transparent font-mono text-[11px] tracking-[0.18em] uppercase px-3"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="banned">Banned</option>
        </select>
      </Card>

      {err && <ErrorBlock>{err}</ErrorBlock>}

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#0A0A0A] text-[#F9F8F6] font-mono text-[10px] tracking-[0.18em] uppercase">
                <th className="text-left p-3">User</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Created</th>
                <th className="text-left p-3">Last login</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-10 text-center text-[#5C5C5C] font-mono text-xs">No users.</td></tr>
              )}
              {data.items.map((u) => (
                <tr key={u.user_id} className="border-t border-[#0A0A0A]/8" data-testid={`user-row-${u.user_id}`}>
                  <td className="p-3">
                    <div className="font-serif text-base">{u.name || "—"}</div>
                    <div className="font-mono text-[10px] text-[#5C5C5C] tracking-wide">{u.user_id}</div>
                  </td>
                  <td className="p-3 font-mono text-xs">{u.email}</td>
                  <td className="p-3 font-mono text-[10px] text-[#5C5C5C]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  <td className="p-3 font-mono text-[10px] text-[#5C5C5C]">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}</td>
                  <td className="p-3"><Status value={u.status} /></td>
                  <td className="p-3">
                    <div className="flex gap-2 justify-end flex-wrap">
                      {can(role, "admin") && (u.status !== "banned" ? (
                        <PillButton tone="danger" data-testid={`ban-${u.user_id}`}
                          onClick={() => setStatusFor(u.user_id, "banned")}
                          disabled={busy === u.user_id + ":banned"}>
                          {busy === u.user_id + ":banned" ? <Spinner /> : <Ban className="w-3 h-3" />} Ban
                        </PillButton>
                      ) : (
                        <PillButton data-testid={`unban-${u.user_id}`}
                          onClick={() => setStatusFor(u.user_id, "active")}
                          disabled={busy === u.user_id + ":active"}>
                          <CheckCircle2 className="w-3 h-3" /> Reinstate
                        </PillButton>
                      ))}
                      {can(role, "super_admin") && (
                        <PillButton tone="danger" data-testid={`delete-${u.user_id}`}
                          onClick={() => deleteUser(u.user_id)} disabled={!!busy}>
                          <Trash2 className="w-3 h-3" /> Delete
                        </PillButton>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-[#0A0A0A]/12 flex items-center justify-between font-mono text-[10px] tracking-[0.18em] uppercase text-[#5C5C5C]">
          <span>Showing {data.skip + 1}–{Math.min(data.skip + data.limit, data.total)} of {data.total}</span>
          <div className="flex gap-2">
            <PillButton onClick={() => setSkip(Math.max(0, skip - data.limit))} disabled={skip === 0}>
              <ChevronLeft className="w-3 h-3" /> Prev
            </PillButton>
            <PillButton onClick={() => setSkip(skip + data.limit)} disabled={skip + data.limit >= data.total}>
              Next <ChevronRight className="w-3 h-3" />
            </PillButton>
          </div>
        </div>
      </Card>
    </div>
  );
};

const BlueprintsPage = ({ role }) => {
  const [data, setData] = useState({ items: [], total: 0, skip: 0, limit: 25 });
  const [q, setQ] = useState("");
  const [flagged, setFlagged] = useState("");
  const [skip, setSkip] = useState(0);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = (s = skip) => {
    setErr(null);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (flagged) params.set("flagged", flagged);
    params.set("skip", s);
    params.set("limit", 25);
    axios.get(`${API}/admin/blueprints?${params}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  };
  useEffect(() => { load(0); setSkip(0); /* eslint-disable-next-line */ }, [flagged]);
  useEffect(() => { load(skip); /* eslint-disable-next-line */ }, [skip]);

  const toggleFlag = async (id) => {
    setBusy(id + ":flag");
    try { await axios.post(`${API}/admin/blueprints/${id}/flag`); load(skip); }
    catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };
  const deleteBp = async (id) => {
    if (!window.confirm("Delete this blueprint permanently?")) return;
    setBusy(id + ":del");
    try { await axios.delete(`${API}/admin/blueprints/${id}`); load(skip); }
    catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-6">
      <div>
        <Eyebrow klein>Blueprints</Eyebrow>
        <h2 className="font-serif text-4xl font-light tracking-tight mt-1">Generated memos — {data.total}.</h2>
      </div>

      <Card className="p-4 flex flex-wrap gap-3">
        <form onSubmit={(e) => { e.preventDefault(); setSkip(0); load(0); }} className="flex items-center border border-[#0A0A0A]/15 flex-1 min-w-[260px]">
          <Search className="w-4 h-4 mx-3 text-[#5C5C5C]" />
          <input
            data-testid="blueprints-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by idea, blueprint id, user id…"
            className="bg-transparent py-2.5 outline-none font-sans text-sm flex-1"
          />
          <button type="submit" className="font-mono text-[10px] tracking-[0.18em] uppercase px-4 py-2 text-[#5C5C5C] hover:text-[#002FA7]">Go</button>
        </form>
        <select
          data-testid="blueprints-flag-filter"
          value={flagged} onChange={(e) => setFlagged(e.target.value)}
          className="border border-[#0A0A0A]/15 bg-transparent font-mono text-[11px] tracking-[0.18em] uppercase px-3"
        >
          <option value="">All</option>
          <option value="true">Flagged only</option>
          <option value="false">Not flagged</option>
        </select>
      </Card>

      {err && <ErrorBlock>{err}</ErrorBlock>}

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#0A0A0A] text-[#F9F8F6] font-mono text-[10px] tracking-[0.18em] uppercase">
                <th className="text-left p-3">Idea</th>
                <th className="text-left p-3">Owner</th>
                <th className="text-left p-3">Created</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-10 text-center text-[#5C5C5C] font-mono text-xs">No blueprints.</td></tr>
              )}
              {data.items.map((b) => (
                <tr key={b.id} className="border-t border-[#0A0A0A]/8" data-testid={`blueprint-row-${b.id}`}>
                  <td className="p-3 max-w-[420px]">
                    <div className="font-serif text-base line-clamp-2">{b.idea || "—"}</div>
                    <div className="font-mono text-[10px] text-[#5C5C5C] mt-1">{b.id}</div>
                  </td>
                  <td className="p-3 font-mono text-[10px]">{b.user_id || <em className="text-[#5C5C5C]">guest</em>}</td>
                  <td className="p-3 font-mono text-[10px] text-[#5C5C5C]">{b.created_at ? new Date(b.created_at).toLocaleString() : "—"}</td>
                  <td className="p-3">
                    {b.flagged ? <span className="font-mono text-[9px] tracking-[0.22em] uppercase px-2 py-1 bg-[#7a0e0e] text-white">Flagged</span> : <span className="font-mono text-[9px] text-[#5C5C5C]">—</span>}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2 justify-end flex-wrap">
                      {can(role, "admin") && (
                        <>
                          <PillButton data-testid={`flag-${b.id}`} onClick={() => toggleFlag(b.id)} disabled={busy === b.id + ":flag"}>
                            <Flag className="w-3 h-3" /> {b.flagged ? "Unflag" : "Flag"}
                          </PillButton>
                          <PillButton tone="danger" data-testid={`del-${b.id}`} onClick={() => deleteBp(b.id)} disabled={busy === b.id + ":del"}>
                            <Trash2 className="w-3 h-3" /> Delete
                          </PillButton>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-[#0A0A0A]/12 flex items-center justify-between font-mono text-[10px] tracking-[0.18em] uppercase text-[#5C5C5C]">
          <span>Showing {data.skip + 1}–{Math.min(data.skip + data.limit, data.total)} of {data.total}</span>
          <div className="flex gap-2">
            <PillButton onClick={() => setSkip(Math.max(0, skip - data.limit))} disabled={skip === 0}>
              <ChevronLeft className="w-3 h-3" /> Prev
            </PillButton>
            <PillButton onClick={() => setSkip(skip + data.limit)} disabled={skip + data.limit >= data.total}>
              Next <ChevronRight className="w-3 h-3" />
            </PillButton>
          </div>
        </div>
      </Card>
    </div>
  );
};

const AdminsPage = ({ self }) => {
  const [admins, setAdmins] = useState([]);
  const [err, setErr] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/admin/admins`).then((r) => setAdmins(r.data.items)).catch((e) => setErr(e?.response?.data?.detail || e.message));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true); setErr(null);
    try { await axios.post(`${API}/admin/admins`, { email: email.trim(), role }); setEmail(""); load(); }
    catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const removeAdmin = async (em) => {
    if (!window.confirm(`Remove ${em} from admins?`)) return;
    try { await axios.delete(`${API}/admin/admins/${encodeURIComponent(em)}`); load(); }
    catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-6">
      <div>
        <Eyebrow klein>Admin Management</Eyebrow>
        <h2 className="font-serif text-4xl font-light tracking-tight mt-1">Who can see this panel.</h2>
        <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#5C5C5C] mt-2">
          Admins sign in with Google. Their email must be on this list to access /admin.
        </p>
      </div>

      <Card className="p-5">
        <Eyebrow klein>Add Admin</Eyebrow>
        <form onSubmit={add} className="mt-3 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[260px]">
            <label className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] block mb-1">Email</label>
            <input
              data-testid="admin-email-input"
              value={email} onChange={(e) => setEmail(e.target.value)}
              type="email" required placeholder="someone@company.com"
              className="w-full border border-[#0A0A0A]/15 bg-transparent px-3 py-2.5 font-sans text-sm focus:border-[#002FA7] outline-none"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] block mb-1">Role</label>
            <select
              data-testid="admin-role-select"
              value={role} onChange={(e) => setRole(e.target.value)}
              className="border border-[#0A0A0A]/15 bg-transparent px-3 py-2.5 font-mono text-xs uppercase tracking-wider"
            >
              <option value="support_manager">Support Manager</option>
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>
          <PillButton tone="klein" disabled={busy} data-testid="admin-add-btn">
            {busy ? <Spinner /> : "Add Admin"}
          </PillButton>
        </form>
      </Card>

      {err && <ErrorBlock>{err}</ErrorBlock>}

      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#0A0A0A] text-[#F9F8F6] font-mono text-[10px] tracking-[0.18em] uppercase">
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3">Added By</th>
              <th className="text-left p-3">Created</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((a) => (
              <tr key={a.email} className="border-t border-[#0A0A0A]/8">
                <td className="p-3 font-mono text-xs">{a.email}{a.email === self?.email && <em className="ml-2 text-[#002FA7]">(you)</em>}</td>
                <td className="p-3"><Status value={a.role} /></td>
                <td className="p-3 font-mono text-[10px] text-[#5C5C5C]">{a.added_by || "—"}</td>
                <td className="p-3 font-mono text-[10px] text-[#5C5C5C]">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</td>
                <td className="p-3 text-right">
                  <PillButton tone="danger" onClick={() => removeAdmin(a.email)} data-testid={`remove-admin-${a.email}`}
                    disabled={a.email === self?.email || a.added_by === "system"}>
                    <Trash2 className="w-3 h-3" /> Remove
                  </PillButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

const AccessLogsPage = () => {
  const [data, setData] = useState({ items: [], total: 0, skip: 0, limit: 50 });
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState({ ip: "", user_id: "", country: "", path: "", status: "" });
  const [skip, setSkip] = useState(0);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = (s = skip) => {
    setLoading(true); setErr(null);
    const p = new URLSearchParams();
    Object.entries(q).forEach(([k, v]) => { if (v) p.set(k, v); });
    p.set("skip", s);
    p.set("limit", 50);
    axios.get(`${API}/admin/access-logs?${p}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };
  const loadStats = () => {
    axios.get(`${API}/admin/access-logs/stats`)
      .then((r) => setStats(r.data))
      .catch(() => { /* stats are best-effort */ });
  };
  useEffect(() => { load(0); setSkip(0); loadStats(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(skip); /* eslint-disable-next-line */ }, [skip]);

  const submit = (e) => { e.preventDefault(); setSkip(0); load(0); };
  const reset = () => { setQ({ ip: "", user_id: "", country: "", path: "", status: "" }); setSkip(0); setTimeout(() => load(0), 0); };

  const statusPill = (s) => {
    if (s == null) return null;
    const bg = s >= 500 ? "bg-[#7a0e0e] text-white"
      : s >= 400 ? "bg-[#EFEFE8] text-[#7a0e0e]"
      : s >= 300 ? "bg-[#EFEFE8] text-[#5C5C5C]"
      : "bg-[#0A0A0A] text-[#F9F8F6]";
    return <span className={`font-mono text-[9px] tracking-[0.18em] px-2 py-1 ${bg}`}>{s}</span>;
  };

  return (
    <div className="space-y-6">
      <div>
        <Eyebrow klein>Access Logs</Eyebrow>
        <h2 className="font-serif text-4xl font-light tracking-tight mt-1">Every request served.</h2>
        <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#5C5C5C] mt-2">
          IP · User-Agent · Geo · User (if signed-in). Recorded since access-log middleware went live.
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KCard accent label="Total Requests" value={stats.total_requests} hint={`${stats.requests_24h} in last 24h`} />
          <KCard label="Unique IPs · Total" value={stats.unique_ips_total} hint={`${stats.unique_ips_24h} in last 24h`} />
          <KCard label="Signed-in Users · 24h" value={stats.unique_users_24h} />
          <KCard label="Requests · 7d" value={stats.requests_7d} />
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="p-5">
            <Eyebrow klein>Top Countries</Eyebrow>
            <ul className="mt-3 divide-y divide-[#0A0A0A]/10">
              {stats.top_countries.length === 0 && <li className="py-3 text-sm text-[#5C5C5C] font-mono">No data yet</li>}
              {stats.top_countries.map((c) => (
                <li key={c.country} className="flex justify-between items-baseline py-2">
                  <span className="font-serif text-base">{c.country}</span>
                  <span className="font-mono text-[10px] text-[#002FA7]">{c.count}</span>
                </li>
              ))}
            </ul>
          </Card>
          <Card className="p-5">
            <Eyebrow klein>Top Endpoints</Eyebrow>
            <ul className="mt-3 divide-y divide-[#0A0A0A]/10">
              {stats.top_paths.slice(0, 8).map((p) => (
                <li key={p.path} className="flex justify-between items-baseline py-2 gap-3">
                  <span className="font-mono text-[11px] truncate">{p.path}</span>
                  <span className="font-mono text-[10px] text-[#002FA7] shrink-0">{p.count}</span>
                </li>
              ))}
            </ul>
          </Card>
          <Card className="p-5">
            <Eyebrow klein>Top IPs</Eyebrow>
            <ul className="mt-3 divide-y divide-[#0A0A0A]/10">
              {stats.top_ips.map((row) => (
                <li key={row.ip} className="flex justify-between items-baseline py-2 gap-3">
                  <div className="min-w-0">
                    <div className="font-mono text-[11px] truncate">{row.ip}</div>
                    <div className="font-mono text-[9px] text-[#5C5C5C] tracking-wide truncate">{[row.city, row.country].filter(Boolean).join(" · ") || "unresolved"}</div>
                  </div>
                  <span className="font-mono text-[10px] text-[#002FA7] shrink-0">{row.count}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}

      <Card className="p-4">
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
          <input data-testid="al-ip" value={q.ip} onChange={(e) => setQ({ ...q, ip: e.target.value })}
            placeholder="IP" className="border border-[#0A0A0A]/15 bg-transparent px-3 py-2 font-mono text-xs" />
          <input data-testid="al-country" value={q.country} onChange={(e) => setQ({ ...q, country: e.target.value })}
            placeholder="Country" className="border border-[#0A0A0A]/15 bg-transparent px-3 py-2 font-sans text-sm" />
          <input data-testid="al-path" value={q.path} onChange={(e) => setQ({ ...q, path: e.target.value })}
            placeholder="Path contains…" className="border border-[#0A0A0A]/15 bg-transparent px-3 py-2 font-mono text-xs" />
          <input data-testid="al-user" value={q.user_id} onChange={(e) => setQ({ ...q, user_id: e.target.value })}
            placeholder="User ID" className="border border-[#0A0A0A]/15 bg-transparent px-3 py-2 font-mono text-xs" />
          <input data-testid="al-status" value={q.status} onChange={(e) => setQ({ ...q, status: e.target.value })}
            placeholder="Status (e.g. 200)" className="border border-[#0A0A0A]/15 bg-transparent px-3 py-2 font-mono text-xs" />
          <div className="flex gap-2">
            <PillButton tone="klein" data-testid="al-apply">Apply</PillButton>
            <PillButton onClick={reset} data-testid="al-reset">Reset</PillButton>
          </div>
        </form>
      </Card>

      {err && <ErrorBlock>{err}</ErrorBlock>}

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#0A0A0A] text-[#F9F8F6] font-mono text-[10px] tracking-[0.18em] uppercase">
                <th className="text-left p-3">When</th>
                <th className="text-left p-3">IP</th>
                <th className="text-left p-3">Location</th>
                <th className="text-left p-3">Path</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">User</th>
                <th className="text-left p-3">User-Agent</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-3 py-10 text-center font-mono text-xs text-[#5C5C5C]"><Spinner /> Loading…</td></tr>}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-12 text-center font-mono text-xs text-[#5C5C5C]">
                  No access log entries match the current filters. If this list is empty, requests will start appearing here once traffic hits the backend after the last deploy.
                </td></tr>
              )}
              {!loading && data.items.map((e) => (
                <tr key={e.id} className="border-t border-[#0A0A0A]/8" data-testid={`al-row-${e.id}`}>
                  <td className="p-3 font-mono text-[10px] text-[#5C5C5C] whitespace-nowrap">{new Date(e.ts).toLocaleString()}</td>
                  <td className="p-3 font-mono text-[11px]">{e.ip}</td>
                  <td className="p-3 font-mono text-[10px] text-[#5C5C5C]">
                    {[e.city, e.country].filter(Boolean).join(" · ") || <em className="text-[#5C5C5C]/60">unresolved</em>}
                  </td>
                  <td className="p-3 font-mono text-[10px] max-w-[240px] truncate" title={e.path}>
                    <span className="text-[#5C5C5C] mr-1">{e.method}</span>{e.path}
                  </td>
                  <td className="p-3">{statusPill(e.status)}</td>
                  <td className="p-3 font-mono text-[10px]">{e.user_id || <em className="text-[#5C5C5C]">guest</em>}</td>
                  <td className="p-3 font-mono text-[9px] text-[#5C5C5C] max-w-[220px] truncate" title={e.user_agent}>{e.user_agent}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-[#0A0A0A]/12 flex items-center justify-between font-mono text-[10px] tracking-[0.18em] uppercase text-[#5C5C5C]">
          <span>Showing {data.total === 0 ? 0 : data.skip + 1}–{Math.min(data.skip + data.limit, data.total)} of {data.total}</span>
          <div className="flex gap-2">
            <PillButton onClick={() => setSkip(Math.max(0, skip - data.limit))} disabled={skip === 0}>
              <ChevronLeft className="w-3 h-3" /> Prev
            </PillButton>
            <PillButton onClick={() => setSkip(skip + data.limit)} disabled={skip + data.limit >= data.total}>
              Next <ChevronRight className="w-3 h-3" />
            </PillButton>
          </div>
        </div>
      </Card>
    </div>
  );
};


const ActivityPage = () => {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/activity?limit=200`)
      .then((r) => setItems(r.data.items))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, []);
  return (
    <div className="space-y-6">
      <div>
        <Eyebrow klein>Audit Logs</Eyebrow>
        <h2 className="font-serif text-4xl font-light tracking-tight mt-1">Every admin action.</h2>
      </div>
      {err && <ErrorBlock>{err}</ErrorBlock>}
      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#0A0A0A] text-[#F9F8F6] font-mono text-[10px] tracking-[0.18em] uppercase">
              <th className="text-left p-3">When</th>
              <th className="text-left p-3">Admin</th>
              <th className="text-left p-3">Action</th>
              <th className="text-left p-3">Target</th>
              <th className="text-left p-3">Before → After</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-12 text-center font-mono text-xs text-[#5C5C5C]">No audit entries yet.</td></tr>
            )}
            {items.map((a) => (
              <tr key={a.id} className="border-t border-[#0A0A0A]/8">
                <td className="p-3 font-mono text-[10px] text-[#5C5C5C] whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</td>
                <td className="p-3 font-mono text-xs">{a.admin_email}</td>
                <td className="p-3 font-mono text-[10px] tracking-[0.18em] uppercase text-[#002FA7]">{a.action}</td>
                <td className="p-3 font-mono text-xs">{a.target || "—"}</td>
                <td className="p-3 font-mono text-[10px] text-[#5C5C5C]">
                  {a.before ? JSON.stringify(a.before) : "—"} → {a.after ? JSON.stringify(a.after) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

// ---- Layout / Shell ----
const SIDEBAR = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", min: "support_manager" },
  { to: "/admin/users", icon: Users, label: "Users", min: "support_manager" },
  { to: "/admin/blueprints", icon: FileText, label: "Blueprints", min: "support_manager" },
  { to: "/admin/access-logs", icon: Globe, label: "Access Logs", min: "support_manager" },
  { to: "/admin/activity", icon: Activity, label: "Audit Logs", min: "support_manager" },
  { to: "/admin/admins", icon: Shield, label: "Admin Management", min: "super_admin" },
];

const AdminShell = ({ ctx, onSignOut }) => {
  const navigate = useNavigate();
  const role = ctx.role;
  const sidebar = useMemo(() => SIDEBAR.filter((s) => can(role, s.min)), [role]);

  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#0A0A0A] flex">
      <aside className="w-60 border-r border-[#0A0A0A]/12 hidden md:flex flex-col bg-[#EFEFE8]/40">
        <div className="px-5 py-5 border-b border-[#0A0A0A]/12">
          <div className="font-serif text-2xl tracking-tight">BluePrint<span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7] ml-1">AI</span></div>
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] mt-1">Admin Console</div>
        </div>
        <nav className="flex-1 py-3" data-testid="admin-sidebar">
          {sidebar.map((s) => {
            const Icon = s.icon;
            const slug = s.to.replace(/^\/admin\/?/, "") || "dashboard";
            return (
              <NavLink
                key={s.to}
                to={s.to}
                end={s.to === "/admin"}
                data-testid={`nav-${slug}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-3 font-mono text-[11px] tracking-[0.18em] uppercase border-l-2 ${
                    isActive ? "border-[#002FA7] text-[#002FA7] bg-[#F9F8F6]" : "border-transparent text-[#0A0A0A]/80 hover:bg-[#F9F8F6]"
                  }`
                }
              >
                <Icon className="w-4 h-4" /> {s.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-[#0A0A0A]/12 p-4 space-y-2">
          <div className="flex items-center gap-2">
            {ctx.user.picture ? (
              <img src={ctx.user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <span className="w-8 h-8 rounded-full bg-[#002FA7] text-white font-mono text-[10px] flex items-center justify-center">
                {(ctx.user.name || ctx.user.email || "?").slice(0, 1).toUpperCase()}
              </span>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-serif text-sm truncate">{ctx.user.name}</div>
              <div className="font-mono text-[9px] tracking-[0.18em] uppercase text-[#5C5C5C]">{ctx.role}</div>
            </div>
          </div>
          <button
            onClick={() => navigate("/")}
            data-testid="admin-back-to-app"
            className="w-full font-mono text-[10px] tracking-[0.22em] uppercase border border-[#0A0A0A]/20 px-3 py-2 hover:border-[#002FA7] hover:text-[#002FA7] flex items-center gap-2"
          >
            <ArrowLeft className="w-3 h-3" /> Back to app
          </button>
          <button
            onClick={onSignOut}
            data-testid="admin-signout"
            className="w-full font-mono text-[10px] tracking-[0.22em] uppercase border border-[#0A0A0A] bg-[#0A0A0A] text-[#F9F8F6] px-3 py-2 hover:bg-[#7a0e0e] hover:border-[#7a0e0e] flex items-center gap-2"
          >
            <LogOut className="w-3 h-3" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 p-6 sm:p-10">
        <Routes>
          <Route path="" element={<Dashboard />} />
          <Route path="users" element={<UsersPage role={role} />} />
          <Route path="blueprints" element={<BlueprintsPage role={role} />} />
          <Route path="access-logs" element={<AccessLogsPage />} />
          <Route path="activity" element={<ActivityPage />} />
          <Route
            path="admins"
            element={can(role, "super_admin")
              ? <AdminsPage self={ctx.user} />
              : <Unauthorized user={ctx.user} reason="role" />}
          />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
};

const Unauthorized = ({ user, reason }) => {
  const navigate = useNavigate();
  const isRoleDenied = reason === "role";
  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#0A0A0A] flex items-center justify-center px-6">
      <div className="text-center max-w-lg">
        <Eyebrow klein>403</Eyebrow>
        <h1 className="font-serif text-5xl sm:text-6xl font-light tracking-tighter mt-2">Unauthorized Access</h1>
        <p className="text-base text-[#0A0A0A]/65 mt-4 leading-relaxed">
          {isRoleDenied
            ? `Your role doesn't include access to this section. Contact a super admin if you need elevated permissions.`
            : user
              ? `${user.email} is not an admin on this platform.`
              : "You're not signed in. The admin console is only available to authorized administrators."}
        </p>
        <div className="mt-8 flex gap-3 justify-center">
          <button
            data-testid="back-home-btn"
            onClick={() => navigate(isRoleDenied ? "/admin" : "/")}
            className="bg-[#0A0A0A] text-[#F9F8F6] font-mono text-[11px] tracking-[0.22em] uppercase px-5 py-3 hover:bg-[#002FA7]"
          >
            {isRoleDenied ? "Back to dashboard" : "Back to BluePrintAI"}
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminApp = () => {
  const [ctx, setCtx] = useState(null); // null = loading, false = forbidden, object = ok
  const [user, setUser] = useState(null);

  const refresh = () => {
    axios.get(`${API}/admin/me`)
      .then((r) => setCtx(r.data))
      .catch(async (e) => {
        // Surface the current end-user (if any) so the 403 page can show their email.
        try {
          const me = await axios.get(`${API}/auth/me`);
          setUser(me.data);
        } catch (_) { /* anonymous */ }
        setCtx(false);
      });
  };
  useEffect(() => { refresh(); }, []);

  const signOut = async () => {
    try { await axios.post(`${API}/auth/logout`, {}); } catch (e) { /* */ }
    window.location.href = "/";
  };

  if (ctx === null) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] text-[#0A0A0A] flex items-center justify-center">
        <div className="font-mono text-xs tracking-[0.22em] uppercase text-[#5C5C5C]"><Spinner /> Loading admin console…</div>
      </div>
    );
  }
  if (ctx === false) {
    return <Unauthorized user={user} />;
  }
  return <AdminShell ctx={ctx} onSignOut={signOut} />;
};

export default AdminApp;
